'use client';

import type {
  AuthSessionHintStatus,
  AuthSessionHintUser,
} from '@/hooks/auth/use-has-auth-session-hint';
import { useHasAuthSessionHint } from '@/hooks/auth/use-has-auth-session-hint';
import type { AuthSessionTransitionToken } from '@/lib/auth/auth-session-observer';
import {
  useAuthSession,
  useAuthSessionReadRequest,
} from '@/lib/auth/auth-session-provider';
import { useAuthSessionObserverPublisher } from '@/lib/auth/use-auth-session-observer-publisher';
import { type ReactNode, useEffect, useRef, useState } from 'react';

export type AuthSessionHintBoundaryState = {
  status: AuthSessionHintStatus;
  currentUser: AuthSessionHintUser | null;
  hasAuthSessionHint: boolean;
  isAuthenticated: boolean;
  shouldAssumeAuthenticated: boolean;
};

type AuthSessionHintBoundaryProps = {
  debugName?: string;
  forceRead?: boolean;
  revalidateOnMount?: boolean;
  // 默认只做本地读取，避免局部组件把全局登录态“接管”走。
  publishToObserver?: boolean;
  transitionToken?: AuthSessionTransitionToken;
  children: (state: AuthSessionHintBoundaryState) => ReactNode;
};

export function AuthSessionHintBoundary({
  debugName = 'anonymous',
  forceRead = false,
  revalidateOnMount = false,
  publishToObserver = false,
  transitionToken,
  children,
}: AuthSessionHintBoundaryProps) {
  const hasAuthSessionHint = useHasAuthSessionHint({ enabled: true });
  const shouldReadAuthSession = forceRead || hasAuthSessionHint;
  const hasStartedReader = useAuthSessionReadRequest(shouldReadAuthSession);
  const { data: session, isPending, refetch } = useAuthSession();
  const [revalidationPhase, setRevalidationPhase] = useState<
    'waiting-for-idle' | 'waiting-for-request' | 'pending' | 'complete'
  >(revalidateOnMount ? 'waiting-for-idle' : 'complete');
  const revalidationRequestedRef = useRef(false);
  const observedSessionRequestRef = useRef(false);
  const currentUser = (session?.user ?? null) as AuthSessionHintUser | null;
  const sessionRequestPending =
    shouldReadAuthSession && (!hasStartedReader || isPending);

  useEffect(() => {
    if (shouldReadAuthSession || !revalidateOnMount) {
      return;
    }

    revalidationRequestedRef.current = false;
    observedSessionRequestRef.current = false;
    setRevalidationPhase('waiting-for-idle');
  }, [revalidateOnMount, shouldReadAuthSession]);

  useEffect(() => {
    if (
      !shouldReadAuthSession ||
      !revalidateOnMount ||
      revalidationPhase !== 'waiting-for-idle'
    ) {
      return;
    }

    if (currentUser) {
      setRevalidationPhase('complete');
      return;
    }

    if (sessionRequestPending) {
      observedSessionRequestRef.current = true;
      return;
    }

    if (observedSessionRequestRef.current) {
      setRevalidationPhase('complete');
      return;
    }

    if (revalidationRequestedRef.current) {
      return;
    }
    revalidationRequestedRef.current = true;
    setRevalidationPhase('waiting-for-request');
    void refetch();
  }, [
    currentUser,
    refetch,
    revalidateOnMount,
    revalidationPhase,
    sessionRequestPending,
    shouldReadAuthSession,
  ]);

  useEffect(() => {
    if (revalidationPhase === 'waiting-for-request' && sessionRequestPending) {
      setRevalidationPhase('pending');
      return;
    }

    if (revalidationPhase === 'pending' && !sessionRequestPending) {
      setRevalidationPhase('complete');
    }
  }, [revalidationPhase, sessionRequestPending]);

  const isRevalidationComplete = revalidationPhase === 'complete';
  const status: AuthSessionHintStatus = !shouldReadAuthSession
    ? 'unauthenticated'
    : sessionRequestPending || !isRevalidationComplete
      ? 'loading'
      : currentUser
        ? 'authenticated'
        : 'unauthenticated';
  const state: AuthSessionHintBoundaryState = {
    status,
    currentUser: status === 'authenticated' ? currentUser : null,
    hasAuthSessionHint,
    isAuthenticated: status === 'authenticated',
    shouldAssumeAuthenticated:
      shouldReadAuthSession && status !== 'unauthenticated',
  };
  const scope = `auth-session-hint-boundary:${debugName}`;

  useAuthSessionObserverPublisher({
    status,
    currentUserId: currentUser?.id ?? null,
    source: scope,
    enabled: publishToObserver && shouldReadAuthSession,
    transitionToken,
  });

  return children(state);
}
